/**
 * AI Configuration Service
 *
 * API calls for managing AI configurations (Platform Admin only)
 */

import axios from 'axios';

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * AI Configuration interface matching backend
 */
export interface AIConfiguration {
  id: string;
  name: string;
  description: string | null;
  configuration_type: string;
  is_active: boolean;
  is_default: boolean;
  model: string;
  temperature: number;
  max_tokens: number;
  system_prompt: string;
  user_prompt_template: string;
  min_requirements: number;
  max_requirements_per_type: number;
  requirement_description_max_length: number;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  version: number;
}

/**
 * Create AI Configuration input
 */
export interface CreateAIConfigurationInput {
  name: string;
  description?: string;
  configuration_type: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  system_prompt: string;
  user_prompt_template: string;
  min_requirements?: number;
  max_requirements_per_type?: number;
  requirement_description_max_length?: number;
}

/**
 * Update AI Configuration input
 */
export interface UpdateAIConfigurationInput {
  name?: string;
  description?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  system_prompt?: string;
  user_prompt_template?: string;
  min_requirements?: number;
  max_requirements_per_type?: number;
  requirement_description_max_length?: number;
  change_description?: string;
}

/**
 * AI Configuration History entry
 */
export interface AIConfigurationHistory {
  id: string;
  configuration_id: string;
  name: string;
  configuration_type: string;
  model: string;
  temperature: number;
  max_tokens: number;
  system_prompt: string;
  user_prompt_template: string;
  min_requirements: number;
  max_requirements_per_type: number;
  requirement_description_max_length: number;
  version: number;
  changed_by: string;
  changed_at: string;
  change_description: string | null;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Get all AI configurations
 */
export async function getAllConfigurations(token: string): Promise<AIConfiguration[]> {
  const response = await axios.get<ApiResponse<AIConfiguration[]>>(
    `${API_BASE_URL}/admin/ai-configurations`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Get AI configurations by type
 */
export async function getConfigurationsByType(
  type: string,
  token: string
): Promise<AIConfiguration[]> {
  const response = await axios.get<ApiResponse<AIConfiguration[]>>(
    `${API_BASE_URL}/admin/ai-configurations/type/${type}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Get configuration by ID
 */
export async function getConfigurationById(
  id: string,
  token: string
): Promise<AIConfiguration> {
  const response = await axios.get<ApiResponse<AIConfiguration>>(
    `${API_BASE_URL}/admin/ai-configurations/${id}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Create new AI configuration
 */
export async function createConfiguration(
  input: CreateAIConfigurationInput,
  token: string
): Promise<AIConfiguration> {
  const response = await axios.post<ApiResponse<AIConfiguration>>(
    `${API_BASE_URL}/admin/ai-configurations`,
    input,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Update AI configuration
 */
export async function updateConfiguration(
  id: string,
  input: UpdateAIConfigurationInput,
  token: string
): Promise<AIConfiguration> {
  const response = await axios.put<ApiResponse<AIConfiguration>>(
    `${API_BASE_URL}/admin/ai-configurations/${id}`,
    input,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Activate configuration (deactivates others of same type)
 */
export async function activateConfiguration(
  id: string,
  token: string
): Promise<AIConfiguration> {
  const response = await axios.post<ApiResponse<AIConfiguration>>(
    `${API_BASE_URL}/admin/ai-configurations/${id}/activate`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Delete AI configuration (cannot delete defaults)
 */
export async function deleteConfiguration(id: string, token: string): Promise<void> {
  await axios.delete(`${API_BASE_URL}/admin/ai-configurations/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

/**
 * Get configuration history
 */
export async function getConfigurationHistory(
  id: string,
  token: string
): Promise<AIConfigurationHistory[]> {
  const response = await axios.get<ApiResponse<AIConfigurationHistory[]>>(
    `${API_BASE_URL}/admin/ai-configurations/${id}/history`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

/**
 * Duplicate configuration
 */
export async function duplicateConfiguration(
  id: string,
  newName: string,
  token: string
): Promise<AIConfiguration> {
  const response = await axios.post<ApiResponse<AIConfiguration>>(
    `${API_BASE_URL}/admin/ai-configurations/${id}/duplicate`,
    { name: newName },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}

export default {
  getAllConfigurations,
  getConfigurationsByType,
  getConfigurationById,
  createConfiguration,
  updateConfiguration,
  activateConfiguration,
  deleteConfiguration,
  getConfigurationHistory,
  duplicateConfiguration
};
